import React, { useEffect, useState } from "react";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { FaCaretDown } from "react-icons/fa";
import { navigateWithFai } from "../utils/Utils";
import { useTranslation } from "react-i18next";


// Ensure shimmer keyframes are injected once
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const AgeCompareSkeleton = ({ isMobile }) => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        backgroundColor: "var(--bg-card)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100%",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        overflow: "hidden",
      }}
    >
      {/* Header shimmer (SectionHeader placeholder) */}
      <div style={{ padding: "16px 20px 0 20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />
          <div
            style={{
              ...shimmerBaseStyle,
              height: 20,
              width: 180,
              borderRadius: 8,
            }}
          />
        </div>
      </div>

      {/* Avatar row shimmer */}
      <div
        style={{
          paddingLeft: "20px",
          paddingRight: "20px",
          display: "flex",
          flexDirection: "row",
          justifyContent: isMobile ? "center" : "space-evenly",
          alignItems: "center",
          padding: isMobile ? "12px 0" : "20px 5%",
          gap: "20px",
        }}
      >
        {/* LEFT CARD SKELETON */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginLeft: "20px",
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              backgroundColor: "#fff",
              borderRadius: 15,
              width: 120,
              height: 120,
              border: "1px solid #e5e5e5",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          />

          <div
            style={{
              marginTop: 10,
              textAlign: "center",
              width: 120,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                borderRadius: 8,
                marginBottom: 6,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                borderRadius: 8,
                width: 90,
                marginInline: "auto",
              }}
            />
          </div>
        </div>

        {/* ARROW SKELETON */}
        <div
          style={{
            padding: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              width: 35,
              height: 35,
              borderRadius: 12,
            }}
          />
        </div>

        {/* RIGHT CARD SKELETON */}
        <div
          style={{
            marginRight: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              backgroundColor: "#fff",
              borderRadius: 20,
              width: 120,
              height: 120,
              border: "1px solid #e5e5e5",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          />

          <div
            style={{
              marginTop: 10,
              textAlign: "center",
              width: 120,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                borderRadius: 8,
                marginBottom: 6,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                borderRadius: 8,
                width: 90,
                marginInline: "auto",
              }}
            />
          </div>
        </div>
      </div>

      {/* Gradient bar skeleton */}
      <div
        style={{
          padding: isMobile ? "0 14px" : "0 15%",
          position: "relative",
          maxWidth: 900,
          marginInline: "auto",
        }}
      >
        <div
          style={{
            height: isMobile ? 40 : 55,
            borderRadius: 50,
            background:
              "linear-gradient(90deg, #FF6A5B 0%, #8A6E3C 50%, #37A845 100%)",
            display: "flex",
            alignItems: "center",
            paddingInline: 20,
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              height: isMobile ? 10 : 14,
              width: "100%",
              borderRadius: 14,
            }}
          />
        </div>

        {/* POINTER + LINE + CIRCLE placeholders (static) */}
        <div
          style={{
            position: "absolute",
            top: isMobile ? 17 : 17,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "12px solid transparent",
            borderRight: "12px solid transparent",
            borderBottom: "18px solid #8A6E3C",
          }}
        />

        <div
          style={{
            position: "absolute",
            top: isMobile ? 22 : 32,
            left: "50%",
            transform: "translateX(-50%)",
            width: 4,
            height: isMobile ? 30 : 46,
            background: "#8A6E3C",
          }}
        />

        <div
          style={{
            position: "absolute",
            top: isMobile ? 13 : 19,
            left: "70%",
            transform: "translateX(-50%)",
            width: isMobile ? 12 : 14,
            height: isMobile ? 12 : 14,
            borderRadius: "50%",
            background: "#3FAE4E",
            border: "2px solid #fff",
          }}
        />
      </div>

      {/* AGE DIFF TEXT SKELETON */}
      <div
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          paddingInline: 20,
          paddingBottom: 20,
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            width: 24,
            height: 24,
            borderRadius: 999,
          }}
        />
        <div
          style={{
            ...shimmerBaseStyle,
            width: 80,
            height: 20,
            borderRadius: 8,
          }}
        />
        <div
          style={{
            ...shimmerBaseStyle,
            width: 180,
            height: 20,
            borderRadius: 8,
          }}
        />
      </div>
    </div>
  );
};

const AgeCompare = ({ onHide } = {}) => {
  const { t } = useTranslation();
  const [ageProfile, setAgeProfile] = useState(null);
  const [images, setImages] = useState(null);

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    lifestyleApi.agecompareData(encryptedId).then((res) => {
      if (res?.data?.code === 200) {
        setAgeProfile(res.data.data.age_profile);
        setImages(res.data.data.img);
      }
    });
  }, []);

  const noAiImage = !!ageProfile && images?.aiImageExists === false;

  useEffect(() => {
    if (noAiImage && onHide) onHide();
  }, [noAiImage]);

  if (!ageProfile) return <AgeCompareSkeleton isMobile={false} />;

  if (noAiImage) {
    if (onHide) return null;

    return (
      <div
        style={{
          backgroundColor: "var(--bg-card)",
          width: "100%",
          borderRadius: "20px",
          border: "1px solid var(--border-subtle)",
          overflow: "hidden",
          padding: "20px",
          textAlign: "center",
          color: "var(--text-secondary)",
        }}
      >
        Age comparison not available for this user yet.
      </div>
    );
  }

  const leftAvatar = images?.actualAgeImg;
  const rightAvatar = "data:image/jpeg;base64," + ageProfile.user_img_64;

  return (
    <div
      style={{
        backgroundColor: "var(--bg-card)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100%",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        overflow: "hidden",
      }}
    >
      <SectionHeader
        title={ageProfile.Title || t("ageCompare.title")}
        icon="https://appcdn.goqii.com/storeimg/56982_1764053060.png"
        subtitle={ageProfile.heading}
       
      />

      <div
        style={{
          paddingLeft: "20px",
          paddingRight: "20px",
          display: "flex",
          flexDirection: "row",
          justifyContent: isMobile ? "center" : "space-evenly",
          alignItems: "center",
          padding: isMobile ? "2px" : "20px 5%",
          gap: "20px",
        }}
      >
        {/* LEFT CARD */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginLeft: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "var(--bg-surface)",
              borderRadius: 15,
              width: 80,
              height: 80,
              border: "1px solid var(--border-subtle)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <img
              src={leftAvatar}
              style={{
                width: "80px",
                height: "80px",
                objectFit: "cover",
              }}
              alt="Sedentary"
            />
          </div>

          <div
            style={{
              marginTop: 10,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: isMobile ? 12 : 12,
                color: "var(--text-primary)",
                lineHeight: 1.2,
              }}
            >
              {t("ageCompare.sedentaryYou")}
            </div>
            <div
              style={{
                color:"var(--text-secondary)",
                marginTop: 4,
                fontSize: isMobile ? 9 : 9,
                lineHeight: 1.3,
              }}
            >
              {t("ageCompare.withoutGoqii")}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="https://appcdn.goqii.com/storeimg/18498_1698909235.png"
            alt="arrow"
            style={{
              width: 35,
            }}
          />
        </div>

        {/* RIGHT CARD */}
        <div
          style={{
            marginRight: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "var(--bg-surface)",
              borderRadius: 20,
              width: 80,
              height: 80,
              border: "1px solid var(--border-subtle)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <img
              src={rightAvatar}
              style={{
                width: "80px",
                height: "80px",
                objectFit: "cover",
              }}
              alt="Current"
            />
          </div>

          <div
            style={{
              marginTop: 10,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: isMobile ? 12 : 12,
                color: "var(--text-primary)",
                lineHeight: 1.2,
              }}
            >
              {t("ageCompare.currentYou")}
            </div>
            <div
              style={{
                color: "var(--text-secondary)",
                marginTop: 4,
                fontSize: isMobile ? 9 : 9,
                lineHeight: 1.3,
              }}
            >
              {t("ageCompare.withGoqii")}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: isMobile ? "0 25px" : "0 15%",
          position: "relative",
          maxWidth: 400,
          marginInline: "auto",
        }}
      >
        <div
          style={{
            height: isMobile ? 20 : 55,
            borderRadius: 50,
            background:
              "linear-gradient(90deg, #FF6A5B 0%, #8A6E3C 50%, #37A845 100%)",
            display: "flex",
            alignItems: "center",
            paddingInline: 20,
          }}
        >
          <div
            style={{
              height: isMobile ? 4 : 14,
              width: "100%",
              background: "var(--bg-surface)",
              borderRadius: 14,
            }}
          />
        </div>

        {/* POINTER */}
        <div
          style={{
            position: "absolute",
            top: isMobile ? 3 : 17,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderBottom: "12px solid #8A6E3C",
          }}
        />

        {/* LINE */}
        <div
          style={{
            position: "absolute",
            top: isMobile ? 20 : 32,
            left: "50%",
            transform: "translateX(-50%)",
            width: 2,
            height: isMobile ? 20 : 46,
            background: "#8A6E3C",
          }}
        />

        {/* CIRCLE */}
        <div
          style={{
            position: "absolute",
            top: isMobile ? 0 : 0,
            left: "70%",
            transform: "translateX(-50%)",
            width: isMobile ? 12 : 14,
            height: isMobile ? 12 : 14,
            borderRadius: "50%",
            background: "#3FAE4E",
            border: "2px solid var(--bg-surface)",
          }}
        />
      </div>

      {/* ---------------- AGE DIFF TEXT ---------------- */}
      <div
        style={{
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          paddingInline: 20,
          paddingBottom: 20,
        }}
      >
        <FaCaretDown style={{ fontSize: isMobile ? 18 : 18, color: "#32A852" }} />

        <span
          style={{
            fontWeight: 700,
            fontSize: isMobile ? 12 : 12,
            color: "#32A852",
          }}
        >
          {ageProfile.age_diff}
        </span>

        <span
          style={{
            fontSize: isMobile ? 11 : 11,
            fontWeight: 500,
            color:"var(--text-secondary)",
          }}
        >
          {ageProfile.diff} than actual age
        </span>
      </div>
    </div>
  );
};

export default AgeCompare;
